/**
 * Fix for React DevTools "history is not defined" error
 *
 * This error occurs when React DevTools tries to hook into the application
 * before the browser's history API is properly initialized or when there's
 * a naming collision in the global scope.
 *
 * This file should be imported before any other imports in the application.
 */

// Ensure the browser's native history object is available
if (typeof window !== 'undefined') {
  // Store a reference to the native history object to prevent it from being overridden
  window.__nativeHistory = window.history;

  // Suppress the specific DevTools error by wrapping console.error
  const originalError = console.error;
  const originalWarn = console.warn;

  console.error = function (...args) {
    // Filter out the "history is not defined" error from DevTools
    const errorMessage = args[0]?.toString() || '';
    const errorString = args.join(' ');

    if (errorMessage.includes('history is not defined') ||
        errorMessage.includes('installHook.js') ||
        errorString.includes('overrideMethod') ||
        errorString.includes('GraphQLCore.js')) {
      // Suppress this specific error - it's a DevTools initialization issue
      // that doesn't affect application functionality
      return;
    }
    // Call the original console.error for other errors
    originalError.apply(console, args);
  };

  console.warn = function (...args) {
    // Also filter warnings that might be related
    const warnMessage = args[0]?.toString() || '';
    if (warnMessage.includes('installHook.js')) {
      return;
    }
    originalWarn.apply(console, args);
  };

  // Ensure window.history is always defined
  if (!window.history) {
    console.warn('Browser history API is not available. Creating a stub.');
    window.history = window.__nativeHistory || {
      pushState: () => {},
      replaceState: () => {},
      go: () => {},
      back: () => {},
      forward: () => {},
      length: 0,
      state: null,
    };
  }

  // Protect against React DevTools trying to access undefined history in its scope
  // This creates a safer environment for DevTools to operate in
  if (typeof window.__REACT_DEVTOOLS_GLOBAL_HOOK__ !== 'undefined') {
    const hook = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
    if (hook && typeof hook.inject === 'function') {
      // DevTools is already loaded, ensure it has proper error handling
      const originalInject = hook.inject;
      hook.inject = function (...args) {
        try {
          return originalInject.apply(this, args);
        } catch (e) {
          // Silently catch DevTools injection errors
          console.debug('React DevTools injection handled:', e.message);
        }
      };
    }
  }
}

